import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
// Common Components
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
// API
import { 
    createPurchaseReturn, 
    fetchPurchaseReturn, 
    updatePurchaseReturn, 
    getPurchaseReturnPrepopulation, 
    getPurchaseInvoicesForReturn 
} from '../../../api/PurchaseReturnsAPI';
import { getParties } from '../../../api/PartiesAPI';
import { getHoas } from '../../../api/HoasAPI';
import { handleApiError } from '../../../api/risapi';
// Hooks & Utils
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';

const initialItem = { 
    id: null, product_id: null, return_uom_id: null, 
    return_quantity: '', return_price: '', 
    conversion_factor: 1, line_total: 0, 
    adjustments: [], max_return_quantity: null, grn_item_id: null 
};

const initialState = {
    party_id: null,
    purchase_invoice_id: null,
    return_date: new Date(),
    billing_address: '', shipping_address: '',
    items: [],
    adjustments: [],
    total_amount: 0,
    source_info: null,
    return_note_number: null
};

const PurchaseReturnForm = ({ formType, currentId, sourceInvoiceId }) => {
    const navigate = useNavigate();
    const [pr, setPr] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    // Only lock the form if specifically in 'view' mode. 'edit' mode should be editable.
    const [isViewOnly, setIsViewOnly] = useState(formType === 'view');
    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);

    const [partyOptions, setPartyOptions] = useState([]);
    const [invoiceOptions, setInvoiceOptions] = useState([]);
    const [gstOptions, setGstOptions] = useState([]);

    // --- 1. Load Masters ---
    useEffect(() => {
        let isMounted = true;
        if (formType === 'create') setLoading(true);
        Promise.all([
            new Promise(r => getParties({paginate: false}, r)),
            new Promise(r => getHoas({paginate: false}, r))
        ]).then(([partyRes, hoaRes]) => {
            if (!isMounted) return;
            if (partyRes.parties) {
                 const parties = Array.isArray(partyRes.parties) ? partyRes.parties : partyRes.parties.data;
                 setPartyOptions(parties.map(p => ({value: p.id, label: p.name})));
            }
            if (hoaRes.hoas) {
                 const hoas = Array.isArray(hoaRes.hoas) ? hoaRes.hoas : hoaRes.hoas.data;
                 setGstOptions(hoas.filter(h => h.is_gst_head).map(h => ({value: h.id, label: h.name})));
            }
            setLoading(false);
        });

        // If viewing/editing existing return, load it
        if (currentId && (formType === 'view' || formType === 'edit')) {
            setLoading(true);
            fetchPurchaseReturn(currentId, (res) => {
                if (!isMounted) return;
                setLoading(false);
                if (res.purchase_return) {
                    const data = res.purchase_return;
                    setPr({
                        ...data,
                        return_date: parseISO(data.return_date),
                        items: data.items.map(i => ({
                             ...i,
                             // Ensure numbers for calculations
                             return_quantity: parseFloat(i.return_quantity),
                             return_price: parseFloat(i.return_price),
                             product_name: i.product?.name,
                             uom_name: i.uom?.short_name,
                             // In edit mode, we might not easily know the original max without re-fetching invoice data.
                             // For simplicity, we might disable increasing quantity beyond what it currently is if we don't have max.
                             max_return_quantity: null 
                        }))
                    });
                    // Ensure we don't forcibly lock it if we are in 'edit' mode
                    setIsViewOnly(formType === 'view');
                    
                    if (data.purchase_invoice) {
                         setInvoiceOptions([{ value: data.purchase_invoice.id, label: data.purchase_invoice.invoice_number }]);
                    }
                }
            });
        }
        return () => { isMounted = false; };
    }, [formType, currentId]);

    // --- 2. Load Invoices when Party changes ---
    useEffect(() => {
        if (!pr.party_id || isViewOnly) return;
        // Avoid infinite loops if option already exists
        if (invoiceOptions.find(opt => opt.value === pr.purchase_invoice_id) && invoiceOptions.length === 1) return;

        setLoading(true);
        getPurchaseInvoicesForReturn(pr.party_id, (invoices) => {
            setLoading(false);
            if (Array.isArray(invoices)) {
                setInvoiceOptions(invoices.map(inv => ({
                    value: inv.id, 
                    label: `${inv.invoice_number} (${inv.invoice_date})`
                })));
            }
        });
    }, [pr.party_id, isViewOnly]);

    // --- 3. Pre-population (URL or Dropdown) ---
    useEffect(() => {
        if (sourceInvoiceId && formType === 'create' && !pr.purchase_invoice_id) {
             handleInvoiceChange({ value: sourceInvoiceId });
        }
    }, [sourceInvoiceId, formType]);

    const handleInvoiceChange = (selectedOption) => {
        const invoiceId = selectedOption ? selectedOption.value : null;
        if (!invoiceId) {
             setPr(prev => ({ ...prev, purchase_invoice_id: null, items: [], source_info: null }));
             return;
        }

        setLoading(true);
        getPurchaseReturnPrepopulation(invoiceId, (res) => {
             setLoading(false);
             if (res.prepopulation_data) {
                 const data = res.prepopulation_data;
                 setPr(prev => ({
                     ...prev,
                     party_id: data.party_id,
                     purchase_invoice_id: data.purchase_invoice_id,
                     source_info: `Invoice #${data.invoice_number} (${data.invoice_date})`,
                     items: data.items.map(item => ({
                         ...initialItem,
                         ...item,
                         return_quantity: 0, // Force user to enter actual quantity to return
                     }))
                 }));
                 if (!selectedOption.label) {
                      setInvoiceOptions([{ value: data.purchase_invoice_id, label: data.invoice_number }]);
                 }
                 setToaster({ type: 'info', heading: 'Data Loaded', message: 'Enter quantities to return.' });
                 setShowToaster(true);
             } else if (res.error) {
                  setPr(prev => ({ ...prev, purchase_invoice_id: null }));
                  setToaster({ type: 'warning', heading: 'Cannot Return', message: res.error.message });
                  setShowToaster(true);
             }
        });
    };

    // --- 4. Calculations ---
    const handleItemChange = (index, field, value) => {
        const newItems = [...pr.items];
        if (field === 'return_quantity') {
             const max = newItems[index].max_return_quantity;
             // Only enforce max if it's known (might be null in simple edit mode)
             if (max !== null && parseFloat(value) > max) {
                  value = max;
                  setToaster({type: 'warning', heading: 'Limit Reached', message: `Max returnable quantity is ${max}`}); setShowToaster(true);
             }
        }
        newItems[index][field] = value;
        
        // Recalculate line total
        const qty = parseFloat(newItems[index].return_quantity) || 0;
        const price = parseFloat(newItems[index].return_price) || 0;
        let baseTotal = qty * price;
        
        // Recalculate taxes (percentage based)
        const newAdjs = (newItems[index].adjustments || []).map(adj => {
             if (adj.percentage) {
                  adj.amount = ((baseTotal * parseFloat(adj.percentage)) / 100).toFixed(2);
             }
             return adj;
        });
        const adjTotal = newAdjs.reduce((sum, adj) => sum + (parseFloat(adj.amount)||0), 0);
        newItems[index].line_total = (baseTotal + adjTotal).toFixed(2);
        newItems[index].adjustments = newAdjs;

        setPr(prev => ({ 
            ...prev, items: newItems,
            total_amount: newItems.reduce((sum, item) => sum + parseFloat(item.line_total||0), 0)
        }));
    };

    // --- Tax Handlers ---
    const addItemTax = (i) => {
        const newItems = [...pr.items]; newItems[i].adjustments.push({ hoa_id: null, percentage: '', amount: '', is_subtraction: false }); setPr({...pr, items: newItems});
    };
    const removeItemTax = (i, j) => {
        const newItems = [...pr.items]; newItems[i].adjustments = newItems[i].adjustments.filter((_, idx) => idx !== j);
        // Trigger recalculation
        handleItemChange(i, 'return_quantity', newItems[i].return_quantity);
    };
    const handleTaxChange = (i, j, f, v) => {
         const newItems = [...pr.items]; newItems[i].adjustments[j][f] = v;
         handleItemChange(i, 'return_quantity', newItems[i].return_quantity);
    };

    // --- Save Handler ---
    const handleSave = () => {
        if (saving || isViewOnly) return;
        setErrors({});
        
        if (!pr.party_id || !pr.purchase_invoice_id) { 
             setErrors({ party_id: !pr.party_id ? 'Required' : null, purchase_invoice_id: !pr.purchase_invoice_id ? 'Required' : null }); 
             return; 
        }
        
        const validItems = pr.items.filter(i => parseFloat(i.return_quantity) > 0);
        if (validItems.length === 0) { 
             setToaster({type: 'danger', message: 'Please enter a return quantity for at least one item.'}); setShowToaster(true); 
             return; 
        }

        setSaving(true);
        const isCreating = formType === 'create';
        const apiCall = isCreating ? createPurchaseReturn : (id, data, cb) => updatePurchaseReturn(id, data, cb);
        
        const payload = {
            ...pr,
            return_date: format(pr.return_date, 'yyyy-MM-dd'),
            items: validItems
        };

        apiCall(isCreating ? payload : currentId, isCreating ? (r) => handleSaveResponse(r) : payload, (response) => handleSaveResponse(response));
    };

    const handleSaveResponse = (res) => {
        setSaving(false);
        if (res.purchase_return) {
             navigate('/franchisee_user/inventory/purchase-returns', { state: { message: `Purchase Return ${formType==='create'?'created':'updated'} successfully.` } });
        } else {
             handleApiError(res, setErrors);
        }
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main className="container-fluid">
                <AppBreadCrumb title={formType === 'create' ? 'New Purchase Return' : (isViewOnly ? 'View Purchase Return' : 'Edit Purchase Return')} />
                {loading && <div className="loader_box"><div className="loader_14"></div></div>}
                <div className="card">
                    <div className="card-header d-flex justify-content-between">
                        <h5>{pr.return_note_number ? `Return #${pr.return_note_number}` : 'Create Purchase Return'}</h5>
                        {pr.source_info && <span className="badge bg-info text-dark align-self-center">Against: {pr.source_info}</span>}
                    </div>
                    <div className="card-body">
                        <form onSubmit={e => e.preventDefault()}>
                            {/* Header */}
                            <div className="row mb-3">
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Supplier *</label>
                                    {/* Locked in edit mode to prevent changing supplier mid-return */}
                                    <Select options={partyOptions} value={partyOptions.find(o => o.value === pr.party_id)} 
                                        onChange={opt => setPr({...pr, party_id: opt?.value, purchase_invoice_id: null, items: [], source_info: null})} 
                                        isDisabled={isViewOnly || formType === 'edit' || !!sourceInvoiceId} 
                                        placeholder="Select Supplier" />
                                    <span className="text-danger small">{errors.party_id}</span>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Against Invoice *</label>
                                    <Select options={invoiceOptions} value={invoiceOptions.find(o => o.value == pr.purchase_invoice_id)} 
                                        onChange={handleInvoiceChange} 
                                        isDisabled={!pr.party_id || isViewOnly || formType === 'edit' || !!sourceInvoiceId} 
                                        placeholder={pr.party_id ? "Select Invoice" : "Select Supplier first"} 
                                        noOptionsMessage={() => "No eligible invoices found"} />
                                    <span className="text-danger small">{errors.purchase_invoice_id}</span>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Return Date *</label>
                                    <DatePicker selected={pr.return_date} onChange={d => setPr({...pr, return_date: d})} className="form-control" dateFormat="yyyy-MM-dd" disabled={isViewOnly}/>
                                </div>
                            </div>

                            {/* Items */}
                            <h6 className="mb-2">Items to Return</h6>
                            <div className="table-responsive mb-3">
                                <table className="table table-bordered table-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{width:'30%'}}>Product</th>
                                            <th style={{width:'15%'}} className="text-end">Return Qty</th>
                                            <th style={{width:'10%'}}>Unit</th>
                                            <th style={{width:'15%'}} className="text-end">Return Price</th>
                                            <th style={{width:'15%'}} className="text-end">Total Debit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pr.items.map((item, i) => (
                                            <React.Fragment key={i}>
                                                <tr>
                                                    <td className="align-middle">
                                                        <input type="text" className="form-control-plaintext py-0 fw-500" value={item.product_name || `Item #${item.product_id}`} readOnly />
                                                    </td>
                                                    <td>
                                                        <div className="input-group input-group-sm">
                                                            <input type="number" className="form-control text-end" value={item.return_quantity} 
                                                                onChange={e => handleItemChange(i, 'return_quantity', e.target.value)} 
                                                                disabled={isViewOnly} placeholder="0" min="0" />
                                                            {item.max_return_quantity !== null && !isViewOnly && 
                                                                <span className="input-group-text text-muted" style={{fontSize:'0.75rem'}}>/ {item.max_return_quantity}</span>
                                                            }
                                                        </div>
                                                    </td>
                                                    <td className="align-middle"><small>{item.uom_name || '-'}</small></td>
                                                    <td>
                                                        <input type="number" className="form-control form-control-sm text-end" value={item.return_price} readOnly disabled />
                                                    </td>
                                                    <td className="text-end align-middle fw-bold">
                                                        {item.line_total || '0.00'}
                                                    </td>
                                                </tr>
                                                {/* Tax Row */}
                                                {(parseFloat(item.return_quantity) > 0 || isViewOnly) && (
                                                    <tr><td colSpan={5} className="bg-light p-2 border-bottom">
                                                        <div className="d-flex align-items-center mb-1">
                                                            <small className="text-muted me-2">Input Tax Reversal:</small>
                                                            {!isViewOnly && <button className="btn btn-xs btn-outline-primary py-0" onClick={() => addItemTax(i)}>+ Add Tax</button>}
                                                        </div>
                                                        {item.adjustments.map((adj, j) => (
                                                            <div className="row gx-1 mb-1" key={j} style={{maxWidth:'500px'}}>
                                                                 <div className="col-6">
                                                                     <Select options={gstOptions} value={gstOptions.find(o => o.value === adj.hoa_id)} onChange={sel => handleTaxChange(i, j, 'hoa_id', sel?.value)} placeholder="Select Tax" className="form-control-sm p-0" isDisabled={isViewOnly} 
                                                                         styles={{ control: base => ({...base, minHeight:'28px', height:'28px'}), valueContainer: base => ({...base, height:'28px', padding:'0 6px'}), indicatorsContainer: base => ({...base, height:'28px'}) }}/>
                                                                 </div>
                                                                 <div className="col-3"><div className="input-group input-group-sm"><input className="form-control" placeholder="%" value={adj.percentage} onChange={e => handleTaxChange(i, j, 'percentage', e.target.value)} disabled={isViewOnly}/><span className="input-group-text">%</span></div></div>
                                                                 <div className="col-3"><input className="form-control form-control-sm text-end" placeholder="Amt" value={adj.amount} readOnly /></div>
                                                                 <div className="col-auto">{!isViewOnly && <button className="btn btn-xs btn-link text-danger py-0" onClick={() => removeItemTax(i,j)}>&times;</button>}</div>
                                                            </div>
                                                        ))}
                                                    </td></tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                        {pr.items.length === 0 && !loading && 
                                            <tr><td colSpan={5} className="text-center text-muted p-4">Select Supplier and Invoice to load items.</td></tr>
                                        }
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer */}
                            <div className="row justify-content-end border-top pt-3">
                                <div className="col-md-4 text-end">
                                    <h5>Total Debit Note Value: <span className="text-primary">{parseFloat(pr.total_amount || 0).toFixed(2)}</span></h5>
                                </div>
                            </div>

                            <div className="text-end mt-3">
                                {!isViewOnly ? (
                                    <>
                                    <button className="btn btn-primary me-2" onClick={handleSave} disabled={saving || pr.total_amount <= 0}>
                                        {saving ? 'Saving...' : (formType === 'create' ? 'Create Return' : 'Update Return')}
                                    </button>
                                    <button className="d-none  d-md-inline-block btn btn-light-reddit b-r-22 d-inline-flex-center" type="button" onClick={() => navigate('/franchisee_user/inventory/purchase-returns')} >
                          
                            Go Back</button>
                                    </>
                                ) : (
                                    <button className="btn btn-danger" onClick={() => navigate('/franchisee_user/inventory/purchase-returns')}>Close View</button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </main>
        </>
    );
};

export default PurchaseReturnForm;